/**
 * <s-product-gallery> — product page media stage with thumbnails.
 *
 * Contract (all scoped to this element):
 *   [data-gallery-track]          — horizontal scroll-snap track (swipe on touch).
 *   [data-gallery-slide]          — one per media item, in order. Optional data-media-id.
 *   [data-gallery-thumb]          — thumbnail buttons with data-index; aria-current marks the active one.
 *   [data-gallery-step="-1|1"]    — previous / next buttons.
 *   [data-gallery-counter]        — optional "n" text node for the active position.
 *   data-variant-media (on host)  — JSON map of variant id → media id, used to jump to a
 *                                   variant's image when a variant radio changes inside the
 *                                   closest [data-product-region].
 */
class ProductGallery extends HTMLElement {
  #abort = null;
  #observer = null;
  #track = null;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #variantMedia = {};

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#track = this.querySelector('[data-gallery-track]');
    this.#slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.#track || this.#slides.length === 0) return;

    try {
      this.#variantMedia = JSON.parse(this.dataset.variantMedia || '{}');
    } catch {
      this.#variantMedia = {};
    }

    this.addEventListener('click', (event) => this.#onClick(event), { signal });

    this.#track.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.#index + (this.#isRtl() ? -1 : 1));
        else if (event.key === 'ArrowLeft') this.goTo(this.#index + (this.#isRtl() ? 1 : -1));
        else return;
        event.preventDefault();
      },
      { signal }
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.#slides.indexOf(entry.target), false);
        }
      },
      { root: this.#track, threshold: 0.6 }
    );
    for (const slide of this.#slides) this.#observer.observe(slide);

    const region = this.closest('[data-product-region]');
    region?.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        const variantId = target.dataset.variantId || target.selectedOptions?.[0]?.dataset.variantId;
        const mediaId = variantId ? this.#variantMedia[variantId] : null;
        if (!mediaId) return;
        const slideIndex = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (slideIndex >= 0) this.goTo(slideIndex);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  goTo(rawIndex) {
    const count = this.#slides.length;
    if (count === 0) return;
    const index = ((rawIndex % count) + count) % count;
    const slide = this.#slides[index];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll the track only (never the page) so the stage swaps in place.
    const trackBox = this.#track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    this.#track.scrollBy({ left: slideBox.left - trackBox.left, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(index, true);
  }

  #onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const thumb = target.closest('[data-gallery-thumb]');
    if (thumb instanceof HTMLElement) {
      this.goTo(Number(thumb.dataset.index || 0));
      return;
    }
    const step = target.closest('[data-gallery-step]');
    if (step instanceof HTMLElement) {
      const direction = Number(step.dataset.galleryStep || 0);
      this.goTo(this.#index + direction);
    }
  }

  #setActive(index, scrollThumb) {
    if (index < 0) return;
    this.#index = index;
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = String(index + 1);
    const activeThumb = this.#thumbs[index];
    const strip = activeThumb?.parentElement?.closest('[data-gallery-thumbs]');
    if (scrollThumb && activeThumb && strip) {
      const stripBox = strip.getBoundingClientRect();
      const thumbBox = activeThumb.getBoundingClientRect();
      if (thumbBox.left < stripBox.left || thumbBox.right > stripBox.right) {
        strip.scrollBy({ left: thumbBox.left - stripBox.left - 8 });
      }
      if (thumbBox.top < stripBox.top || thumbBox.bottom > stripBox.bottom) {
        strip.scrollBy({ top: thumbBox.top - stripBox.top - 8 });
      }
    }
  }

  #isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', ProductGallery);
